import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Plus, Loader2, Receipt, ChevronRight, FileEdit } from 'lucide-react';
import { gbp, ukDate, STATUS_STYLE, STATUS_LABEL } from '../../components/portal/invoice/invoiceUtils';
import { useCompanySplit, ToInvoiceChips, createUrl } from '../../components/portal/invoice/CompanySplit';

const FILTERS = [['all', 'All'], ['draft', 'Drafts'], ['open', 'Sent'], ['paid', 'Paid']];
const matches = (inv, f) => f === 'all' || (f === 'open' ? ['issued', 'sent', 'approved'].includes(inv.status) : inv.status === f);

export default function PortalInvoice() {
  const { api } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [period, setPeriod] = useState(null);
  const { split } = useCompanySplit(api, period, !!period);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [r, s] = await Promise.all([api.get('/portal/invoices2'), api.get('/portal/invoices2/period-suggestion').catch(() => null)]);
      setItems(r.data.items || []);
      if (s) setPeriod({ start: s.data.period_start, end: s.data.period_end });
    }
    catch (e) { toast.error(e.response?.data?.detail || 'Could not load invoices'); }
    finally { setLoading(false); }
  }, [api]);
  useEffect(() => { load(); }, [load]);

  const shown = items.filter(i => matches(i, filter));
  const open = (inv) => navigate(inv.legacy ? `/portal/invoice/${inv.id}` : inv.status === 'draft' ? `/portal/invoice/${inv.id}/edit` : `/portal/invoice/${inv.id}`);

  return (
    <div className="p-4 space-y-4" data-testid="invoice-list-page">
      <Button onClick={() => navigate('/portal/invoice/create')} className="w-full h-14 bg-[#F5A623] text-[#1E2530] hover:bg-[#D98E1D] font-bold text-base" data-testid="new-invoice-btn">
        <Plus className="w-5 h-5 mr-2" /> New invoice
      </Button>
      <ToInvoiceChips split={split} onPick={(c) => navigate(createUrl(c.company, period))} />
      <div className="flex gap-2 overflow-x-auto pb-0.5">
        {FILTERS.map(([k, l]) => (
          <button key={k} type="button" onClick={() => setFilter(k)} className={`px-3 py-1.5 rounded-full text-xs border whitespace-nowrap ${filter === k ? 'bg-white text-[#1E2530] border-white font-semibold' : 'bg-white/5 border-white/10 text-gray-300'}`} data-testid={`invoice-filter-${k}`}>
            {l} <span className="opacity-60">({items.filter(i => matches(i, k)).length})</span>
          </button>
        ))}
      </div>
      {loading ? <div className="py-10 text-center"><Loader2 className="w-6 h-6 animate-spin text-gray-400 mx-auto" /></div> : shown.length === 0 ? (
        <div className="py-12 text-center text-gray-500" data-testid="invoice-empty"><Receipt className="w-10 h-10 mx-auto mb-2 opacity-40" />No invoices here yet.</div>
      ) : (
        <div className="space-y-2" data-testid="invoice-list">
          {shown.map(inv => (
            <button key={inv.id} type="button" onClick={() => open(inv)} className="w-full text-left rounded-xl bg-[#2A3240] border border-white/10 p-3 flex items-center gap-3 active:scale-[0.99]" data-testid={`invoice-row-${inv.id}`}>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-white font-bold">{inv.invoice_number || 'Draft'}</span>
                  {inv.status === 'draft' && <FileEdit className="w-3.5 h-3.5 text-gray-400" />}
                  <Badge variant="outline" className={`text-[10px] ${STATUS_STYLE[inv.status] || STATUS_STYLE.draft}`}>{STATUS_LABEL[inv.status] || inv.status}</Badge>
                </div>
                <div className="text-xs text-gray-400 truncate">
                  {inv.customer?.name || '—'}{inv.customer?.company ? ` · ${inv.customer.company}` : ''}
                  {inv.period_start ? ` · ${ukDate(inv.period_start)} – ${ukDate(inv.period_end)}` : inv.period_month ? ` · ${inv.period_month}` : ''}
                </div>
                {inv.issue_date && <div className="text-[11px] text-gray-500">Issued {ukDate(inv.issue_date)}{inv.due_date ? ` · due ${ukDate(inv.due_date)}` : ''}</div>}
              </div>
              <div className="text-right">
                <div className="text-white font-bold tabular-nums">{gbp(inv.gross_total)}</div>
                <div className="text-[11px] text-gray-500 tabular-nums">payable {gbp(inv.net_payable)}</div>
              </div>
              <ChevronRight className="w-4 h-4 text-gray-500" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
